/**
 * Plans: what each tier includes, and what an account can do right now.
 *
 * A trial gives an account Farm Pro until trialEndsAt; after that it drops back to the plan FMS
 * put it on. FMS waiving payment (billing "free") changes what is charged, not what the plan
 * allows. Limits are checked on the server; the app reads the same rules to show locks and
 * upgrade prompts before anyone hits a wall.
 *
 * Prices here are the launch sheet to be confirmed by Kane; there is no online payment yet, so
 * an upgrade is a request that FMS staff apply (see /plan-requests).
 */
import type { Account, Plan } from "./model.ts";

export type Feature = "control" | "programs" | "insights" | "ask-ai" | "map" | "crops" | "reports-csv" | "api";

export interface PlanInfo {
  id: Plan;
  name: string;
  /** Dollars a month inc. GST; null = by quote. */
  price: number | null;
  /** Devices included; null = unlimited. */
  devices: number | null;
  /** Dollars a month per device over the included number; null = not allowed over. */
  extraDevice: number | null;
  users: number | null;
  /** How far back readings and reports go, in days; null = everything kept. */
  historyDays: number | null;
  smsPerMonth: number;
  features: Feature[];
  /** How many pumping programs the plan allows, by what starts them; null = unlimited. */
  programs: { rules: number; schedules: number } | null;
  blurb: string;
}

export const PLAN_INFO: Record<Plan, PlanInfo> = {
  // The id stays "monitor" (it's what accounts have stored); the name is Basic.
  monitor: {
    id: "monitor",
    name: "Basic",
    price: 39,
    devices: 3,
    extraDevice: null,
    users: 2,
    historyDays: 30,
    smsPerMonth: 0,
    features: ["control", "programs", "map"],
    programs: { rules: 2, schedules: 1 },
    blurb: "See your tanks, pumps and sensors, switch them, and set two rules and a schedule.",
  },
  farm: {
    id: "farm",
    name: "Farm",
    price: 129,
    devices: 15,
    extraDevice: 8,
    users: 5,
    historyDays: 365,
    smsPerMonth: 200,
    features: ["control", "programs", "insights", "map", "crops", "reports-csv"],
    programs: null,
    blurb: "Switch pumps, run programs and keep a year of history.",
  },
  "farm-pro": {
    id: "farm-pro",
    name: "Farm Pro",
    price: 199,
    devices: 30,
    extraDevice: 4,
    users: null,
    historyDays: 5 * 365,
    smsPerMonth: 500,
    features: ["control", "programs", "insights", "ask-ai", "map", "crops", "reports-csv", "api"],
    programs: null,
    blurb: "Everything, with Ask AI, five years of history and the API.",
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    price: null,
    devices: null,
    extraDevice: 0,
    users: null,
    historyDays: null,
    smsPerMonth: 1000,
    features: ["control", "programs", "insights", "ask-ai", "map", "crops", "reports-csv", "api"],
    programs: null,
    blurb: "Several farms, your own branding and a service agreement.",
  },
};

export const PLAN_ORDER: Plan[] = ["monitor", "farm", "farm-pro", "enterprise"];

export const FEATURE_TEXT: Record<Feature, { title: string; body: string }> = {
  control: { title: "Switch pumps and valves", body: "Turn outputs on and off from your phone, with safety timers on the board." },
  programs: { title: "Pumping programs", body: "Start pumps on a schedule or a reading, step through valves and stop on a condition." },
  insights: { title: "Insights", body: "Think-Control learns what's normal for each sensor and pump and tells you what's changed." },
  "ask-ai": { title: "Ask AI", body: "Ask about your farm in plain words and get answers from your own readings." },
  map: { title: "Farm map", body: "Every device on satellite imagery of your property, with live readings and radio links." },
  crops: { title: "Crops", body: "Book each pump run to a crop and see water, power and cost per crop." },
  "reports-csv": { title: "Reports and CSV", body: "Water used, run hours, energy and cost by day, downloadable for your spreadsheet." },
  api: { title: "API and integrations", body: "Connect Think-Control to your other systems with API keys and webhooks." },
};

export interface Entitlement {
  /** The plan FMS put the account on. */
  plan: Plan;
  /** What applies right now: Farm Pro during a trial. */
  effective: Plan;
  trialEndsAt?: number;
  trialDaysLeft?: number;
  features: Feature[];
  devices: number | null;
  users: number | null;
  historyDays: number | null;
  programs: { rules: number; schedules: number } | null;
}

/** True while the account is on a trial of a higher plan. */
export function onTrial(account: Pick<Account, "trialEndsAt">, now: number): boolean {
  return account.trialEndsAt !== undefined && account.trialEndsAt > now;
}

/** What an account can do right now. FMS staff (no account) can do everything. */
export function entitlement(account: Pick<Account, "plan" | "trialEndsAt"> | null | undefined, now: number): Entitlement {
  if (!account) {
    const p = PLAN_INFO.enterprise;
    return { plan: "enterprise", effective: "enterprise", features: p.features, devices: null, users: null, historyDays: null, programs: null };
  }
  const trial = onTrial(account, now);
  const effective: Plan = trial && PLAN_ORDER.indexOf(account.plan) < PLAN_ORDER.indexOf("farm-pro") ? "farm-pro" : account.plan;
  const p = PLAN_INFO[effective];
  return {
    plan: account.plan,
    effective,
    ...(trial ? { trialEndsAt: account.trialEndsAt, trialDaysLeft: Math.max(1, Math.ceil((account.trialEndsAt! - now) / 86_400_000)) } : {}),
    features: p.features,
    devices: p.devices,
    users: p.users,
    historyDays: p.historyDays,
    programs: p.programs,
  };
}

/** A program is a "schedule" when any of its start conditions is a time; otherwise a "rule". */
export function programKind(p: { start: Array<{ type: string }> }): "schedule" | "rule" {
  return p.start.some((s) => s.type === "schedule" || s.type === "once") ? "schedule" : "rule";
}

/**
 * Why a program can't be added on this plan, or undefined when it can. `others` are the
 * account's other programs (not the one being edited).
 */
export function programLimitProblem(
  account: Pick<Account, "plan" | "trialEndsAt"> | null | undefined,
  program: { start: Array<{ type: string }> },
  others: Array<{ start: Array<{ type: string }> }>,
  now: number,
): string | undefined {
  const limit = entitlement(account, now).programs;
  if (!limit) return undefined;
  const kind = programKind(program);
  const used = others.filter((o) => programKind(o) === kind).length;
  const max = kind === "schedule" ? limit.schedules : limit.rules;
  if (used < max) return undefined;
  const plan = PLAN_INFO[entitlement(account, now).effective].name;
  return `The ${plan} plan includes ${limit.rules} rule${limit.rules === 1 ? "" : "s"} and ${limit.schedules} schedule${limit.schedules === 1 ? "" : "s"}. Ask about upgrading in Admin → Plan.`;
}

export function hasFeature(account: Pick<Account, "plan" | "trialEndsAt"> | null | undefined, feature: Feature, now: number): boolean {
  return entitlement(account, now).features.includes(feature);
}

/** The cheapest plan that has a feature: what to suggest upgrading to. */
export function planFor(feature: Feature): Plan {
  return PLAN_ORDER.find((p) => PLAN_INFO[p].features.includes(feature)) ?? "farm-pro";
}

/** The earliest time readings may be shown for an account (history limit), or 0 for all. */
export function historyFrom(account: Pick<Account, "plan" | "trialEndsAt"> | null | undefined, now: number): number {
  const days = entitlement(account, now).historyDays;
  return days === null ? 0 : now - days * 86_400_000;
}

/** Monthly charge for a plan with this many devices, in dollars; null when it's by quote. */
export function monthlyCharge(plan: Plan, deviceCount: number): number | null {
  const p = PLAN_INFO[plan];
  if (p.price === null) return null;
  const extra = p.devices === null ? 0 : Math.max(0, deviceCount - p.devices);
  return p.price + extra * (p.extraDevice ?? 0);
}
